import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"
import { markingCombinations } from "./marking_plan.mjs"

const t = (key, options) => I18n.t(`erp.sales.js.marking_plan.${key}`, options)

const ITEM = "[data-erp--estimate-editor-target='item']"
const MARKING = "[data-erp--estimate-editor-target='marking']"
const ALLOCATION_ROW = "[data-erp--marking-form-target='allocationRow']"

const escape = value => String(value ?? "").replace(/[&<>"']/g, char => `&#${char.charCodeAt(0)};`)
const shown = element => element.style.display !== "none" && !element.classList.contains("hidden")

// Marking chips, "Plan de marquage" and "Mêmes marquages que…" of each textile card (`.tex__marks` and `.plan` of
// the mockup). The plan is another view of the size allocations held by the marking cards (step 4 of their
// wizard): a cell writes the allocation field, then erp--marking-form prices it and erp--autosave saves it.
// Connects to data-controller="erp--marking-plan", on the editor grid next to erp--estimate-editor.
export default class extends Controller {
  static targets = [ "chipTemplate" ]
  static values = { cellClasses: String }

  connect() {
    this._open = new Set()
    this._form = this.element.closest("form")
    this._onSaved = () => this.refresh()
    this._form?.addEventListener("erp--autosave:request-finished", this._onSaved)
    this.refresh()
  }

  disconnect() {
    this._form?.removeEventListener("erp--autosave:request-finished", this._onSaved)
    clearTimeout(this._timer)
  }

  // A wizard step or a save changes many fields at once: one refresh once they are all done
  refresh() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      const markings = this.#markings()
      const items = this.#items()
      items.forEach(item => this.#render(item, items, markings))
    }, 0)
  }

  toggle(event) {
    const id = this.#itemId(event.target.closest(ITEM))
    if (!id) return

    if (this._open.has(id)) this._open.delete(id)
    else this._open.add(id)
    this.refresh()
  }

  // One cell: the pieces of a size carrying a marking. The allocation field it writes bubbles up instead.
  cellChanged(event) {
    event.stopPropagation()
    const cell = event.target
    const field = this.#field(cell.dataset.markingIndex, cell.dataset.variantId)
    if (!field) return

    const quantity = Math.min(parseInt(field.max) || 0, Math.max(0, parseInt(cell.value) || 0))
    if (cell.value !== "" && String(quantity) !== cell.value) cell.value = quantity
    cell.toggleAttribute("data-filled", quantity > 0)
    this.#write(field, quantity)
  }

  // "Tout" / "Aucun" of a column: every size of the line, all or none of its pieces
  fillColumn(event) {
    const { markingIndex, fill } = event.params
    this.#sizes(event.target.closest(ITEM)).forEach(size => {
      const field = this.#field(markingIndex, size.variantId)
      if (field) this.#write(field, fill ? parseInt(field.max) || 0 : 0)
    })
  }

  // "Mêmes marquages que…": every marking of the other line that suits this one, on all its pieces
  copy(event) {
    const select = event.target
    const target = select.closest(ITEM)
    const source = this.#items().find(item => this.#itemId(item) === select.value)
    select.value = ""
    if (!target || !source) return

    const copied = this.#copyMarkings(source, target)
    if (copied > 0) this._open.add(this.#itemId(target))
    const status = target.querySelector("[data-marking-plan-status]")
    if (status) status.textContent = copied > 0 ? t("copied", { count: copied, textile: this.#itemLabel(source) }) : t("nothing_to_copy")
    this.refresh()
  }

  // Returns how many markings were copied
  #copyMarkings(source, target) {
    const sourceSizes = this.#sizes(source)
    const targetSizes = this.#sizes(target)
    const copied = this.#markings().filter(marking => this.#pieces(marking, sourceSizes) > 0 && this.#compatible(marking, target))
    copied.forEach(marking => targetSizes.forEach(size => {
      const field = marking.fields.get(size.variantId)
      if (field) this.#write(field, parseInt(field.max) || 0)
    }))
    return copied.length
  }

  #write(field, quantity) {
    field.value = quantity
    field.dispatchEvent(new Event("input", { bubbles: true }))
  }

  #render(item, items, markings) {
    const sizes = this.#sizes(item)
    const applied = markings.filter(marking => this.#pieces(marking, sizes) > 0)
    const others = items.filter(other => other !== item && markings.some(marking => this.#pieces(marking, this.#sizes(other)) > 0))
    const open = applied.length > 0 && this._open.has(this.#itemId(item))

    this.#renderChips(item, applied)
    this.#renderToggle(item, applied, open)
    this.#renderCopy(item, applied, others)
    const panel = item.querySelector("[data-marking-plan-panel]")
    if (panel) this.#renderPanel(panel, { item, markings, applied, sizes, others, open })
  }

  #renderToggle(item, applied, open) {
    const toggle = item.querySelector("[data-marking-plan-toggle]")
    if (!toggle) return

    toggle.hidden = applied.length === 0
    toggle.setAttribute("aria-expanded", String(open))
    toggle.querySelector("span").textContent = open ? t("hide") : t("show")
  }

  // A line without marking offers to take those of another line, a line with markings does it from its plan
  #renderCopy(item, applied, others) {
    const copy = item.querySelector("[data-marking-plan-copy]")
    if (!copy) return

    copy.hidden = applied.length > 0 || others.length === 0
    this.#fillCopyOptions(copy.querySelector("select"), others)
  }

  #renderPanel(panel, { item, markings, applied, sizes, others, open }) {
    panel.hidden = !open
    if (!open) {
      panel.replaceChildren()
      return
    }

    const columns = markings.filter(marking => applied.includes(marking) || this.#compatible(marking, item))
    // Typing in a cell keeps its table: only the other values and the combinations follow
    if (panel.contains(document.activeElement) && panel.querySelector("table")) {
      this.#syncCells(panel, columns, sizes)
    } else {
      panel.innerHTML = this.#planHtml(item, columns, sizes, others)
      this.#fillCopyOptions(panel.querySelector("select"), others)
    }
    panel.querySelector("[data-marking-plan-combinations]").innerHTML = this.#combinationsHtml(columns, sizes)
  }

  #renderChips(item, applied) {
    const container = item.querySelector("[data-marking-plan-chips]")
    if (!container || !this.hasChipTemplateTarget) return

    container.innerHTML = applied
      .map(marking => this.chipTemplateTarget.innerHTML.replace("__LABEL__", escape(marking.name)))
      .join("")
  }

  #fillCopyOptions(select, others) {
    if (!select) return

    const placeholder = select.options[0]?.outerHTML || ""
    select.innerHTML = placeholder + others.map(other => {
      return `<option value="${escape(this.#itemId(other))}">${escape(this.#itemLabel(other))}</option>`
    }).join("")
  }

  #planHtml(item, columns, sizes, others) {
    const label = this.#itemLabel(item)
    const head = columns.map(marking => `
      <th scope="col" class="border-b border-erp-line bg-erp-surface-soft px-2.5 py-2 text-center align-bottom whitespace-nowrap">
        <span class="flex flex-col text-xs font-extrabold">${escape(marking.technique)}<small class="font-semibold text-erp-muted">${escape(marking.zone)}</small></span>
        <span class="mt-1 block text-[11px] text-erp-muted">
          <button type="button" class="${this.#linkClasses}" data-action="erp--marking-plan#fillColumn"
                  data-erp--marking-plan-marking-index-param="${escape(marking.index)}" data-erp--marking-plan-fill-param="true"
                  aria-label="${escape(t("all_label", { marking: marking.name }))}">${escape(t("all"))}</button> ·
          <button type="button" class="${this.#linkClasses}" data-action="erp--marking-plan#fillColumn"
                  data-erp--marking-plan-marking-index-param="${escape(marking.index)}" data-erp--marking-plan-fill-param="false"
                  aria-label="${escape(t("none_label", { marking: marking.name }))}">${escape(t("none"))}</button>
        </span>
      </th>`).join("")

    const rows = sizes.map(size => `
      <tr>
        <th scope="row" class="border-b border-erp-line px-2.5 py-2 text-left font-bold whitespace-nowrap">
          ${escape(size.label)} <small class="pl-2.5 font-medium text-erp-muted">${escape(t("pieces", { count: size.quantity }))}</small>
        </th>
        ${columns.map(marking => this.#cellHtml(marking, size)).join("")}
      </tr>`).join("")

    const copy = others.length === 0 ? "" : `
      <label class="flex min-w-60 flex-col gap-1.5 text-xs font-bold max-[900px]:min-w-0 max-[900px]:basis-full">
        ${escape(t("copy_from"))}
        <select class="h-10 rounded-erp-field border border-erp-line-strong bg-erp-field px-3 text-[13px] font-semibold focus:border-erp-black focus:outline-none"
                data-action="change->erp--marking-plan#copy">
          <option value="">${escape(t("copy_placeholder"))}</option>
        </select>
      </label>`

    return `
      <div class="overflow-auto rounded-[14px] border border-erp-line" tabindex="0" role="region"
           aria-label="${escape(t("table_label", { textile: label }))}">
        <table class="w-full border-separate border-spacing-0 text-[13px] [&_tbody_tr:last-child>*]:border-b-0">
          <thead><tr>
            <th scope="col" class="border-b border-erp-line bg-erp-surface-soft px-2.5 py-2 text-left align-bottom">${escape(t("size"))}</th>${head}
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div class="flex flex-wrap items-start justify-between gap-5">
        <div class="flex min-w-[260px] flex-1 flex-col gap-2 max-[900px]:min-w-0">
          <span class="text-xs font-extrabold uppercase tracking-[0.08em] text-erp-muted">${escape(t("result"))}</span>
          <span class="text-xs text-erp-muted">${escape(t("result_hint"))}</span>
          <ul class="m-0 flex list-none flex-col gap-1.5 p-0" data-marking-plan-combinations aria-live="polite"></ul>
        </div>
        ${copy}
      </div>`
  }

  #cellHtml(marking, size) {
    const field = marking.fields.get(size.variantId)
    if (!field) return `<td class="border-b border-erp-line px-2.5 py-2 text-center text-erp-muted">—</td>`

    const value = this.#allocated(marking, size.variantId)
    return `
      <td class="border-b border-erp-line px-2.5 py-2 text-center">
        <input type="number" min="0" max="${escape(field.max)}" inputmode="numeric" autocomplete="off" placeholder="0"
               value="${value || ""}" class="${escape(this.cellClassesValue)} h-8! w-[52px]! text-[13px]!" ${value > 0 ? "data-filled" : ""}
               aria-label="${escape(t("cell_label", { marking: marking.name, size: size.label, count: size.quantity }))}"
               data-marking-index="${escape(marking.index)}" data-variant-id="${escape(size.variantId)}"
               data-action="input->erp--marking-plan#cellChanged">
      </td>`
  }

  #syncCells(panel, columns, sizes) {
    panel.querySelectorAll("input[data-marking-index]").forEach(cell => {
      if (cell === document.activeElement) return

      const marking = columns.find(column => column.index === cell.dataset.markingIndex)
      const value = marking ? this.#allocated(marking, cell.dataset.variantId) : 0
      cell.value = value || ""
      cell.toggleAttribute("data-filled", value > 0)
    })
  }

  #combinationsHtml(columns, sizes) {
    const rows = sizes.map(size => ({
      quantity: size.quantity,
      allocations: Object.fromEntries(columns.map(marking => [ marking.index, this.#allocated(marking, size.variantId) ]))
    }))
    const names = Object.fromEntries(columns.map(marking => [ marking.index, marking.name ]))

    return markingCombinations(rows, columns.map(marking => marking.index)).map(({ markings, pieces }) => {
      const label = markings.length > 0 ? markings.map(index => names[index]).join(" + ") : t("unmarked")
      return `<li class="flex items-baseline gap-2.5 text-sm ${markings.length === 0 ? "text-erp-st-pending-fg" : ""}">
        <b class="min-w-16 font-extrabold">${escape(t("pieces", { count: pieces }))}</b><span>${escape(label)}</span></li>`
    }).join("")
  }

  get #linkClasses() {
    return "cursor-pointer font-bold text-erp-ink underline underline-offset-[3px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erp-black"
  }

  // ── Reading the editor ───────────────────────────────────────

  #items() {
    return [ ...this.element.querySelectorAll(ITEM) ].filter(shown)
  }

  #itemId(item) {
    return item?.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value || ""
  }

  #itemLabel(item) {
    const text = target => item.querySelector(`[data-erp--estimate-editor-target='${target}']`)?.textContent?.trim()
    return text("itemSummaryRef") || text("itemSummaryDescription") || ""
  }

  // Saved sizes only: a marking allocates pieces of a size the estimate already holds
  #sizes(item) {
    if (!item) return []

    return [ ...item.querySelectorAll("[data-size-row]") ].filter(row => this.#savedSize(row)).map(row => ({
      variantId: row.querySelector("input[type='hidden'][name$='[id]']").value,
      quantity: parseInt(row.querySelector("[data-size-quantity]")?.value) || 0,
      label: row.querySelector("[data-size-label]")?.textContent?.trim() || ""
    }))
  }

  #savedSize(row) {
    const saved = Boolean(row.querySelector("input[type='hidden'][name$='[id]']")?.value)
    return saved && row.querySelector("input[name$='[_destroy]']")?.value !== "1"
  }

  #markings() {
    return [ ...this.element.querySelectorAll(MARKING) ].filter(shown).map(element => {
      const { technique, zone } = this.#markingLabels(element)
      const name = [ technique, zone ].filter(Boolean).join(" · ") || t("untyped")
      return { element, index: element.dataset.markingIndex, technique: technique || t("untyped"), zone, name, fields: this.#allocationFields(element) }
    })
  }

  #markingLabels(element) {
    const technique = element.querySelector("[data-erp--marking-form-target='techniqueBadge']")?.textContent?.trim() || ""
    const zoneBadge = element.querySelector("[data-erp--marking-form-target='positionBadge']")
    return { technique, zone: zoneBadge && !zoneBadge.hidden ? zoneBadge.textContent.trim() : "" }
  }

  // Size id → its allocation quantity field, for the sizes the marking card still shows
  #allocationFields(element) {
    return new Map([ ...element.querySelectorAll(ALLOCATION_ROW) ].filter(shown).map(row => [
      row.querySelector("input[name$='[estimate_item_variant_id]']")?.value,
      row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
    ]))
  }

  #field(markingIndex, variantId) {
    return this.#markings().find(marking => marking.index === String(markingIndex))?.fields.get(variantId)
  }

  #allocated(marking, variantId) {
    const field = marking.fields.get(variantId)
    const destroyed = field?.closest(ALLOCATION_ROW)?.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value === "1"
    return field && !destroyed ? parseInt(field.value) || 0 : 0
  }

  #pieces(marking, sizes) {
    return sizes.reduce((total, size) => total + this.#allocated(marking, size.variantId), 0)
  }

  // erp--marking-form rates each line of the wizard against the technique and zone
  #compatible(marking, item) {
    const row = marking.element.querySelector(`[data-allocation-item-id='${CSS.escape(this.#itemId(item))}'] [data-erp--marking-form-target='compatibilityRow']`)
    return row?.dataset.compatibilityState !== "incompatible"
  }
}
